/**
 * The paper register.
 *
 * A coach at the side of a pitch marks a sheet on a clipboard, and types it up
 * later or never. The product's job is to print a sheet worth carrying — the
 * right players, the right dates, enough room to write — and to take it back
 * afterwards without retyping it.
 *
 * Pure, so the shape of a month can be tested without a canvas or a database.
 */

import { addDays, daysBetween, fromISODate } from '@shared/dates';
import { shortWeekday } from './calendar';

export interface RegisterColumn {
  /** 'YYYY-MM-DD'. */
  date: string;
  /** Day of month, as printed in the header. */
  day: number;
  /** Two letters for the weekday, in the active locale. */
  weekday: string;
  /** True when something is already scheduled, so the coach sees which days matter. */
  scheduled: boolean;
  /**
   * What was scheduled. A match day is marked differently on the sheet and in
   * the app, because "he missed a Tuesday" and "he missed the match" are not
   * the same sentence to a coach reading a month.
   */
  kind: 'training' | 'match' | null;
  isWeekend: boolean;
}

export interface RegisterPlayer {
  id: string;
  name: string;
  shirtNumber?: number | null;
  positionKey?: string | null;
}

export interface Register {
  from: string;
  to: string;
  columns: RegisterColumn[];
  players: RegisterPlayer[];
}

/** Every day in the period. A register with gaps is one a coach cannot use. */
export function registerColumns(
  from: string,
  to: string,
  locale: string,
  scheduledDates: readonly string[] = [],
  matchDates: readonly string[] = [],
): RegisterColumn[] {
  const span = daysBetween(from, to);
  if (span < 0) return [];

  const scheduled = new Set(scheduledDates);
  const fixtures = new Set(matchDates);
  const weekday = shortWeekday(locale);

  const columns: RegisterColumn[] = [];
  for (let i = 0; i <= span; i++) {
    const date = addDays(from, i);
    const d = fromISODate(date);
    const dow = d.getUTCDay();
    columns.push({
      date,
      day: d.getUTCDate(),
      // Two letters: thirty columns across A4 have no room for "Wednesday".
      weekday: weekday(d),
      // A fixture counts as scheduled even with no session on the day.
      scheduled: scheduled.has(date) || fixtures.has(date),
      kind: fixtures.has(date) ? 'match' : scheduled.has(date) ? 'training' : null,
      isWeekend: dow === 0 || dow === 6,
    });
  }
  return columns;
}

/** The first and last day of a month, as the register's default period. */
export function monthBounds(year: number, month: number): { from: string; to: string } {
  const first = new Date(Date.UTC(year, month, 1));
  const last = new Date(Date.UTC(year, month + 1, 0));
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
}

/**
 * How many columns fit on one landscape A4 page at a legible size.
 *
 * A full month is 31 columns, which fits; a half-season does not, so the sheet
 * splits rather than shrinking the writing box to nothing.
 */
export const MAX_COLUMNS_PER_PAGE = 31;

/**
 * One letter per state, for the grid on screen and the boxes on paper.
 *
 * The same table for both: a sheet that marks a late arrival `L` and a screen
 * that marks it something else is two notations for one squad, and the sheet is
 * the one that gets filled in by somebody who is not the coach.
 */
export const ATTENDANCE_MARK: Record<string, string> = {
  present: '\u2713',
  absent: '\u2717',
  late: 'L',
  excused: 'E',
  injured: 'I',
  modified: 'M',
};

/**
 * How many players one landscape register holds at a size somebody can write in.
 *
 * The sheet gives its rows whatever height is left after the header, the column
 * labels, the note and the footer — 144mm, measured in the browser — and a row of
 * 2.9mm type will not compress below about 4mm however little padding it is
 * given. That puts the ceiling at 36. This sits two rows under it, because the
 * measurement was taken in one font on one machine and a sheet that overflows a
 * page it was told to fit is a sheet with rows missing from the bottom.
 *
 * Past this count the register goes back to being two honest pages rather than
 * one nobody can use.
 */
export const REGISTER_ROWS_PER_PAGE = 34;

/**
 * Whether this squad gets the one-page sheet or ordinary pagination.
 *
 * The caller cannot ask for one page and be wrong about it: a sheet told to be
 * exactly one page crops whatever does not fit, and a register missing its last
 * four players is worse than a register on two pages.
 */
export function registerFitsOnePage(playerCount: number): boolean {
  return playerCount > 0 && playerCount <= REGISTER_ROWS_PER_PAGE;
}

export function splitColumns(columns: readonly RegisterColumn[]): RegisterColumn[][] {
  if (columns.length <= MAX_COLUMNS_PER_PAGE) return [[...columns]];
  const pages: RegisterColumn[][] = [];
  for (let i = 0; i < columns.length; i += MAX_COLUMNS_PER_PAGE) {
    pages.push(columns.slice(i, i + MAX_COLUMNS_PER_PAGE));
  }
  return pages;
}
